import { ArrowLeft2, ArrowRight2 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

type WizardReviewStepState = 'current' | 'done' | 'pending';

interface WizardReviewStepFooterProps {
    total: number;
    currentIndex: number;
    onPrevious?: () => void;
    onNext?: () => void;
    onSelect?: (index: number) => void;
    getStepState?: (index: number) => WizardReviewStepState;
    previousLabel?: string;
    nextLabel?: string;
    stepLabel?: (index: number) => string;
    children?: ReactNode;
}

export function WizardReviewStepFooter({
    total,
    currentIndex,
    onPrevious,
    onNext,
    onSelect,
    getStepState,
    previousLabel = 'Resultado anterior',
    nextLabel = 'Próximo resultado',
    stepLabel = (index) => `Resultado ${index + 1}`,
    children,
}: WizardReviewStepFooterProps) {
    const lastIndex = Math.max(total - 1, 0);
    const safeCurrentIndex = Math.min(Math.max(currentIndex, 0), lastIndex);

    if (total <= 1) {
        return <div className="flex items-center justify-end gap-2">{children}</div>;
    }

    const resolveStepState = (index: number): WizardReviewStepState => {
        if (index === safeCurrentIndex) return 'current';
        return getStepState?.(index) ?? 'pending';
    };

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-center">
            <div className="flex items-center gap-1">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={onPrevious}
                    disabled={safeCurrentIndex === 0 || !onPrevious}
                    aria-label={previousLabel}
                    title={previousLabel}
                >
                    <ArrowLeft2 className="size-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={onNext}
                    disabled={safeCurrentIndex === lastIndex || !onNext}
                    aria-label={nextLabel}
                    title={nextLabel}
                >
                    <ArrowRight2 className="size-4" />
                </Button>
            </div>

            <div className="flex items-center justify-center gap-1.5">
                {Array.from({ length: total }).map((_, index) => {
                    const state = resolveStepState(index);

                    return (
                        <button
                            key={index}
                            type="button"
                            onClick={() => onSelect?.(index)}
                            disabled={!onSelect}
                            aria-label={stepLabel(index)}
                            aria-current={state === 'current' ? 'step' : undefined}
                            className={cn(
                                'h-2 rounded-full transition-all',
                                state === 'current'
                                    ? 'w-6 bg-blue-500'
                                    : state === 'done'
                                      ? 'w-2 bg-green-400'
                                      : 'w-2 bg-border-strong',
                                onSelect ? 'cursor-pointer hover:bg-blue-300' : 'cursor-default',
                            )}
                        />
                    );
                })}
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
                {children}
            </div>
        </div>
    );
}
